import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { FrameViewer, type Frame } from './FrameViewer';
import type { ModelStageId } from '../types/work';
const STAGES: {
  id: ModelStageId;
  label: string;
  title: string;
  note: string;
}[] = [{
  id: 'model',
  label: '02',
  title: 'Reconstruct',
  note: 'What comes back from multi-image reconstruction, before any material work.'
}, {
  id: 'mesh',
  label: '03',
  title: 'Mesh',
  note: 'The topology it actually built, with the baked atlas painted back onto it.'
}, {
  id: 'rig',
  label: '04',
  title: 'Rig',
  // Twenty-four, not the forty quoted further down the page: this is the walk
  // export, which carries only the spec joints the clip actually drives. The
  // hair, brow and socket bones live on the finished avatar.
  note: 'Skin dropped back so the skeleton reads. Twenty-four joints — the spec skeleton the clip drives; the finished avatar carries forty once hair and brows are on it.'
}, {
  id: 'walk',
  label: '05',
  title: 'Animate',
  note: 'A banked clip playing on the result — nothing here was authored for him.'
}];

/**
 * Fetch a model without trusting its extension.
 *
 * The bytes are currently served as `model/gltf-binary` from a `.glb`, which is
 * the easy case. This stays byte-sniffing anyway: the host's accepted file types
 * are not ours to control, and if the model ever has to be smuggled in under
 * another extension — renamed to an image, or base64'd inside JSON — that is a
 * change of URL rather than a change of code.
 *
 * The content type is never consulted, because it cannot be relied on and
 * `fetch` does not care: `nosniff` governs `<img>` and `<script>`, not
 * `arrayBuffer()`. A GLB served as `image/png` decodes exactly the same.
 */
async function fetchModel(src: string): Promise<ArrayBuffer> {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`model ${response.status}`);
  const buffer = await response.arrayBuffer();
  const head = new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength));

  // 0x67 0x6c 0x54 0x46 — 'glTF'. Already binary, nothing to unwrap.
  if (head[0] === 0x67 && head[1] === 0x6c && head[2] === 0x54 && head[3] === 0x46) {
    return buffer;
  }

  // '{' — JSON. Either a base64 envelope we wrote, or a real .gltf document,
  // which GLTFLoader parses from the raw buffer just as happily.
  if (head[0] === 0x7b) {
    const text = new TextDecoder().decode(buffer);
    let envelope: {
      format?: string;
      data?: string;
      bytes?: number;
    };
    try {
      envelope = JSON.parse(text);
    } catch {
      return buffer;
    }
    if (envelope.format !== 'glb-base64' || !envelope.data) return buffer;
    const binary = atob(envelope.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

    // A truncated upload decodes without complaint and then fails much later
    // inside the parser, where the error says nothing useful.
    if (envelope.bytes && envelope.bytes !== bytes.length) {
      throw new Error(`model truncated: ${bytes.length} of ${envelope.bytes}`);
    }
    return bytes.buffer;
  }
  throw new Error('model is neither GLB nor JSON');
}

/**
 * The real model, staged.
 *
 * This is the same character the write-up is about, loaded and rendered rather
 * than photographed: the reader can turn it, and the walk is the actual clip
 * driving the actual skeleton. It is a website, so it may as well show the thing
 * instead of a picture of the thing.
 *
 * Costs about two megabytes, which is only affordable because the source was 22:
 * the textures went to WebP at half resolution, the geometry through meshopt, and
 * the provider's normal map — measured as noise rather than detail — was dropped
 * on the way. Nothing is fetched until the reader scrolls it into view.
 *
 * Falls back to `frames` whenever the model cannot be shown: no WebGL, no URL,
 * a failed fetch. Given no frames it renders nothing at all, which is only the
 * right answer for a supplementary block — see the type.
 */
export function ModelStage({
  src,
  frames,
  stages,
  hold = 4200
}: {
  src: string;
  frames: Frame[];
  stages?: ModelStageId[];
  hold?: number;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(!src);
  const [near, setNear] = useState(false);

  // An unknown or empty selection falls back to the full sequence rather than
  // rendering a viewer with no stages in it.
  const shown = useMemo(() => {
    const picked = stages ? STAGES.filter(stage => stages.includes(stage.id)) : STAGES;
    return picked.length ? picked : STAGES;
  }, [stages]);
  const stage = shown[Math.min(at, shown.length - 1)];

  // What the frame loop reads. Kept in a ref so changing stage does not tear the
  // scene down and rebuild it.
  const wanted = useRef<ModelStageId>(stage.id);
  wanted.current = stage.id;
  const dragging = useRef(false);
  const spin = useRef(0);
  const lastX = useRef(0);

  // Nothing is fetched until it is worth fetching. Two megabytes is cheap for a
  // reader who reaches this section and pure waste for one who does not.
  useEffect(() => {
    const node = holder.current;
    if (!node || !src) return;
    const watcher = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setNear(true);
        watcher.disconnect();
      }
    }, {
      rootMargin: '300px'
    });
    watcher.observe(node);
    return () => watcher.disconnect();
  }, [src]);
  useEffect(() => {
    if (!near || !src || !canvas.current || !holder.current) return;
    const box = holder.current;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas: canvas.current,
        antialias: true,
        alpha: true
      });
    } catch {
      setFailed(true);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(28, 16 / 9, 0.05, 100);
    const turntable = new THREE.Group();
    scene.add(turntable);
    scene.add(new THREE.HemisphereLight(0xffffff, 0xc8ccd6, 1.3));
    const key = new THREE.DirectionalLight(0xfff6e8, 2.1);
    key.position.set(-2.4, 4.2, 3.4);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xdce6ff, 0.5);
    fill.position.set(3, 1.6, 2);
    scene.add(fill);
    const floor = new THREE.Mesh(new THREE.CircleGeometry(0.6, 48), new THREE.MeshBasicMaterial({
      color: 0xdfe3ea,
      transparent: true,
      opacity: 0.5
    }));
    floor.rotation.x = -Math.PI / 2;
    turntable.add(floor);
    let alive = true;
    let raf = 0;
    let mixer: THREE.AnimationMixer | null = null;
    let bones: THREE.SkeletonHelper | null = null;
    let skins: {
      node: THREE.Mesh;
      dressed: THREE.Material;
      bare: THREE.MeshStandardMaterial;
      wires: THREE.MeshBasicMaterial;
    }[] = [];
    const resize = () => {
      const rect = box.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = rect.width / rect.height;
      camera.updateProjectionMatrix();
    };
    const watcher = new ResizeObserver(resize);
    watcher.observe(box);
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    fetchModel(src).then(buffer => loader.parseAsync(buffer, '')).then(gltf => {
      if (!alive) return;
      const root = gltf.scene;

      // Gathered first, mutated after: `traverse` walks children as it finds
      // them, so adding the wireframe inside the callback means it then visits
      // the wireframe — also a mesh — and clones one of those, forever.
      const meshes: THREE.Mesh[] = [];
      root.traverse(node => {
        if ((node as THREE.Mesh).isMesh) meshes.push(node as THREE.Mesh);
      });
      for (const node of meshes) {
        node.frustumCulled = false;
        const dressed = node.material as THREE.Material;
        const bare = new THREE.MeshStandardMaterial({
          color: 0xd6d9df,
          roughness: 0.82,
          metalness: 0
        });
        const wires = new THREE.MeshBasicMaterial({
          color: 0x2b3a52,
          wireframe: true,
          transparent: true,
          opacity: 0
        });
        skins.push({
          node,
          dressed,
          bare,
          wires
        });
        const ghost = (node as THREE.SkinnedMesh).isSkinnedMesh ? new THREE.SkinnedMesh(node.geometry, wires) : new THREE.Mesh(node.geometry, wires);
        if ((node as THREE.SkinnedMesh).isSkinnedMesh) {
          const skinned = node as THREE.SkinnedMesh;
          (ghost as THREE.SkinnedMesh).bind(skinned.skeleton, skinned.bindMatrix);
        }
        ghost.frustumCulled = false;
        node.add(ghost);
      }

      // Not parented to the turntable: `SkeletonHelper` pins its own matrix to
      // the model's `matrixWorld` and turns auto-update off, so putting it
      // under a rotating group applies that rotation twice and the bones drift
      // out of the body.
      bones = new THREE.SkeletonHelper(root);
      bones.material.transparent = true;
      bones.material.opacity = 0;
      bones.material.depthTest = false;
      bones.visible = false;
      scene.add(bones);
      const bounds = new THREE.Box3().setFromObject(root);
      const size = bounds.getSize(new THREE.Vector3());
      const centre = bounds.getCenter(new THREE.Vector3());
      root.position.sub(new THREE.Vector3(centre.x, bounds.min.y, centre.z));
      turntable.add(root);
      const back = size.y * 1.12 / (2 * Math.tan(28 * Math.PI / 360));
      camera.position.set(0, size.y * 0.55, back);
      camera.lookAt(0, size.y * 0.48, 0);
      if (gltf.animations.length) {
        mixer = new THREE.AnimationMixer(root);
        mixer.clipAction(gltf.animations[0]).play();
      }
      resize();
      setReady(true);
    }).catch(() => {
      if (alive) setFailed(true);
    });
    let last = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const delta = Math.min((now - last) / 1000, 1 / 20);
      last = now;
      const showing = wanted.current;
      if (!dragging.current && !calm) spin.current += delta * 0.16;
      turntable.rotation.y = spin.current;

      // The walk is the only stage that advances the clip. Everywhere else the
      // model holds its rest pose, so the mesh and the skeleton read clearly.
      if (mixer) {
        if (showing === 'walk') mixer.update(delta);else mixer.setTime(0);
      }
      for (const part of skins) {
        const wires = showing === 'mesh' ? 0.5 : 0;
        part.wires.opacity += (wires - part.wires.opacity) * 0.12;
        part.node.material = showing === 'model' ? part.bare : part.dressed;
        const faded = showing === 'rig';
        const target = faded ? 0.3 : 1;
        const material = part.dressed as THREE.MeshStandardMaterial;
        if (material.transparent !== faded) {
          material.transparent = faded;
          material.needsUpdate = true;
        }
        material.opacity += (target - material.opacity) * 0.12;
      }
      if (bones) {
        bones.visible = showing === 'rig';
        const want = showing === 'rig' ? 0.95 : 0;
        bones.material.opacity += (want - bones.material.opacity) * 0.12;
      }
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      watcher.disconnect();
      renderer.dispose();
      scene.traverse(node => {
        const mesh = node as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
      });
      skins = [];
    };
  }, [near, src]);

  // Auto-advance, unless the reader has taken over, asked for less motion, or
  // there is only one stage to sit on.
  useEffect(() => {
    if (!playing || !ready || shown.length < 2) return;
    const timer = window.setTimeout(() => setAt(current => (current + 1) % shown.length), hold);
    return () => window.clearTimeout(timer);
  }, [at, playing, ready, hold, shown.length]);
  if (failed) return frames.length ? <FrameViewer frames={frames} data-id="element-34" data-mp="2rn3b28wgh4fd" /> : null;
  return <div data-id="element-35" data-mp="2mt7jbo6scdsn">
      <div ref={holder} onPointerDown={event => {
      dragging.current = true;
      lastX.current = event.clientX;
      event.currentTarget.setPointerCapture(event.pointerId);
    }} onPointerMove={event => {
      if (!dragging.current) return;
      spin.current += (event.clientX - lastX.current) * 0.01;
      lastX.current = event.clientX;
    }} onPointerUp={event => {
      dragging.current = false;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }} className="relative aspect-video cursor-grab overflow-hidden bg-surface active:cursor-grabbing" style={{
      touchAction: 'pan-y'
    }} data-id="element-36" data-mp="2mtgzyl5xx8lo">
        <canvas ref={canvas} className="block h-full w-full" data-id="element-37" data-mp="160lmdzwuftsf" />
        {!ready && <div className="absolute inset-0 grid place-items-center text-[13px] text-ink-faint" data-id="element-38" data-mp="1b2171qv3zyaa">
            loading the model…
          </div>}
        {ready && <span className="pointer-events-none absolute bottom-3 right-3 text-[11px] text-ink-faint" data-id="element-39" data-mp="12d2mpxphdv87">
            drag to turn
          </span>}
      </div>

      {/* A stepper with one button is just a label, so a single-stage viewer
          skips it and lets the note underneath do the talking. */}
      {shown.length > 1 && <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-40" data-mp="174kmwjjojrs4">
          {shown.map((item, index) => <button key={item.id} type="button" aria-current={index === at} onClick={() => {
        setPlaying(false);
        setAt(index);
      }} className={`rounded-md border px-2.5 py-1 text-[12px] transition-colors duration-150 ease-expo ${index === at ? 'border-ink/20 text-ink' : 'border-rule text-ink-faint hover:text-ink-muted'}`} data-id="element-41" data-mp="jeqbm6f0eb9z" data-mpi={index}>
              <span className="tabular-nums opacity-60" data-id="element-42" data-mp="1zv2nndwxlh2d">{item.label}</span> {item.title}
            </button>)}
          <button type="button" onClick={() => setPlaying(current => !current)} className="ml-auto rounded-md border border-rule px-2.5 py-1 text-[12px] text-ink-faint transition-colors duration-150 ease-expo hover:text-ink" data-id="element-43" data-mp="2cb2cptdby9cb">
            {playing ? 'Pause' : 'Play'}
          </button>
        </div>}

      <p className="mt-3 text-[13px] leading-relaxed text-ink-faint" data-id="element-44" data-mp="2q3a393e0t42k">{stage.note}</p>
    </div>;
}